import {
  Box,
  Checkbox,
  Chip,
  Container,
  GridLegacy as Grid,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { CinemaModel, CinemasResponse } from '@/logic/services/cinemas';
import { FC, ReactElement, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCinemas, useCities, useMoviesType } from '@/logic/state';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { visuallyHidden } from '@mui/utils';
import { Film } from '@/logic/services';
import { MovieTypesEnum } from '@/constants/movie-types-enum';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import MoviesList from '../shared/movies-list';

interface MoviesListProps {
  moviesList: Film[];
  mainCinemasList: CinemasResponse;
  total?: number;
  pagination?: { limit: number; page: number };
  fetchMovies: () => void;
  hasMore?: boolean;
  isFetching?: boolean;
  selectedMoviesType: MovieTypesEnum;
}
export const MoviesSection: FC<MoviesListProps> = ({
  moviesList,
  mainCinemasList,
  hasMore,
  isFetching,
  fetchMovies,
  selectedMoviesType,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const cities = useCities(s => s.cities, shallow);
  const setMoviesType = useMoviesType(s => s.setMoviesType, shallow);

  const [cinemasList, setCinemasList] = useState<Partial<CinemaModel>[]>([]);
  const [selectedCinemas, setSelectedCinemas] = useCinemas(
    s => [s.selectedCinemas, s.setSelectedCinemas],
    shallow
  );

  const selectAllCinemas = (): void => {
    const allCinemasIds = cinemasList.map(_value => _value.id);
    setSelectedCinemas(allCinemasIds);
  };

  const handleChange = (
    event: SelectChangeEvent<typeof selectedCinemas>,
    child: ReactElement
  ): void => {
    const {
      target: { value },
    } = event;
    if (child.props.value === 0) {
      if (child.props.children[0].props.checked === true) {
        setSelectedCinemas([]);
        // handelSelectedCinemas([]);
      } else {
        selectAllCinemas();
      }
      return;
    }
    if (
      child.props.value !== 0 &&
      (value as number[]).length === cinemasList.length - 1 &&
      child.props.children[0].props.checked === false
    ) {
      setSelectedCinemas(cinemasList.map(_value => _value.id));
    } else {
      const _allCinemasIndex = selectedCinemas.findIndex(_value => _value === 0);
      if (_allCinemasIndex != -1) (value as number[]).splice(_allCinemasIndex, 1);
      setSelectedCinemas(value as number[]);
    }
  };

  // These effects used to key on JSON.stringify(...) of their inputs, which
  // serialized each array on every render just to build a comparison key —
  // pure main-thread cost in the post-hydration window. The object references
  // are equivalent keys for free: react-query's structural sharing keeps
  // mainCinemasList's identity stable across refetches that return the same
  // data, and cinemasList / cities.citiesList only get new identities when
  // actually set.
  useEffect(() => {
    if (mainCinemasList) {
      const allCinemas: Partial<CinemaModel> = {
        id: 0,
        name: intl.formatMessage({
          id: 'all_cinemas',
        }),
      };
      setCinemasList([allCinemas, ...mainCinemasList.cinema]);
      setSelectedCinemas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainCinemasList]);

  useEffect(() => {
    if (selectedCinemas.length === 0 && !!mainCinemasList)
      setSelectedCinemas([0, ...mainCinemasList.cinema.map(cinema => cinema.id)]);
    else setSelectedCinemas(selectedCinemas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cinemasList]);

  useEffect(() => {
    selectAllCinemas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities.citiesList]);

  const chipSx: Partial<SxProps<Theme>> = {
    height: { mobile: '40px', tablet: pxToRem(43) },
    minWidth: { mobile: '139px', tablet: pxToRem(155) },
    cursor: 'pointer',
    fontWeight: pxToRem(400),
    lineHeight: {
      mobile: '24px',
      tablet: pxToRem(24),
    },
    fontSize: { mobile: '16px', tablet: pxToRem(18) },
    borderRadius: '40px',
  };

  return (
    <Container
      component='section'
      sx={{
        maxWidth: '1784px !important',
        paddingX: {
          mobile: '16px !important',
          largeTablet: '30px !important',
          largeDesktop: '60px !important',
        },
      }}
    >
      <Grid
        container
        sx={{
          alignItems: 'center',
        }}
      >
        <Grid
          item
          mobile={12}
          tablet={6}
          sx={{
            order: { mobile: 2, tablet: 1 },
            marginTop: { mobile: '24px', tablet: 0 },
          }}
        >
          <Stack
            direction='row'
            sx={{
              width: '100%',
              justifyContent: { mobile: 'space-between', tablet: 'flex-start' },
            }}
          >
            <Chip
              label={<FormattedMessage id='movies.now.showing' />}
              variant={
                selectedMoviesType === MovieTypesEnum.SHOW_NOW
                  ? 'selected'
                  : 'not-selected'
              }
              sx={chipSx}
              onClick={() => {
                setMoviesType(MovieTypesEnum.SHOW_NOW);
              }}
            />
            <Chip
              label={<FormattedMessage id='movies.coming.soon' />}
              variant={
                selectedMoviesType === MovieTypesEnum.COMING_SOON
                  ? 'selected'
                  : 'not-selected'
              }
              sx={chipSx}
              onClick={() => {
                setMoviesType(MovieTypesEnum.COMING_SOON);
              }}
            />
          </Stack>
        </Grid>
        <Grid
          item
          mobile={12}
          tablet={6}
          sx={{
            order: {
              mobile: 1,
              tablet: 2,
            },
            display: { tablet: 'flex' },
            justifyContent: { tablet: 'flex-end' },
          }}
        >
          {/*
            `labelId` pointed at an element that was never rendered — this
            Select has no visible label, only a renderValue — so MUI emitted a
            dangling aria-labelledby and the combobox had no accessible name.
            An aria-label does not fix it either: MUI spreads unknown props
            onto the wrapper, and always self-references the combobox id in
            aria-labelledby, which wins over aria-label. So give the id
            something real to point at.
          */}
          <Box
            component='span'
            id='cinema-select-label'
            sx={visuallyHidden as SxProps<Theme>}
          >
            {intl.formatMessage({ id: 'global.select_cinema' })}
          </Box>
          <Select<number[]>
            labelId='cinema-select-label'
            id='cinema-select'
            value={selectedCinemas}
            multiple
            onChange={(
              event: SelectChangeEvent<typeof selectedCinemas>,
              child: ReactElement
            ) => handleChange(event, child)}
            fullWidth
            displayEmpty
            sx={{
              backgroundColor: theme.palette.lightGrey.main,
              maxWidth: {
                tablet: pxToRem(440),
              },
              border: 'none',
              outline: 'none',
              '.MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            MenuProps={{
              BackdropProps: {
                sx: {
                  backgroundColor: 'transparent',
                  backdropFilter: 'none',
                },
              },
              PaperProps: {
                sx: {
                  backgroundColor: '#f1f2f5d1',
                  boxShadow:
                    '0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0.893452px 17.869px rgba(0, 0, 0, 0.160949)',
                  backdropFilter: 'blur(10px)',
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
            renderValue={(selectedCinemas: number[]) => {
              if (
                selectedCinemas.length > 1 &&
                selectedCinemas.length !== cinemasList.length
              ) {
                return (
                  <Stack direction='row'>
                    <Typography
                      sx={{
                        color: theme.palette.typography.color.secondary,
                        fontWeight: '400',
                        fontSize: {
                          mobile: '16px',
                          tablet: pxToRem(16),
                        },
                      }}
                    >
                      <FormattedMessage id='cinema' />
                      :&nbsp;
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: '400',
                        fontSize: {
                          mobile: '16px',
                          tablet: pxToRem(16),
                        },
                      }}
                    >
                      {selectedCinemas.length} &nbsp;
                      <FormattedMessage id='selected.cinemas' />
                    </Typography>
                  </Stack>
                );
              }
              if (
                selectedCinemas.length === cinemasList.length ||
                selectedCinemas.length === 0
              ) {
                return (
                  <Stack direction='row'>
                    <Typography
                      sx={{
                        color: theme.palette.typography.color.secondary,
                        fontWeight: '400',
                        fontSize: {
                          mobile: '16px',
                          tablet: pxToRem(16),
                        },
                      }}
                    >
                      <FormattedMessage id='cinema' />
                      :&nbsp;
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: '400',
                        fontSize: {
                          mobile: '16px',
                          tablet: pxToRem(16),
                        },
                      }}
                    >
                      <FormattedMessage id='all_cinemas' />
                    </Typography>
                  </Stack>
                );
              } else {
                return (
                  <Stack direction='row'>
                    <Typography
                      sx={{
                        color: theme.palette.typography.color.secondary,
                        fontWeight: '400',
                        fontSize: {
                          mobile: '16px',
                          tablet: pxToRem(16),
                        },
                      }}
                    >
                      <FormattedMessage id='cinema' />
                      :&nbsp;
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: '400',
                        fontSize: {
                          mobile: '16px',
                          tablet: pxToRem(16),
                        },
                      }}
                    >
                      {
                        mainCinemasList?.cinema.find(
                          cinema => cinema.id === selectedCinemas[0]
                        )?.name
                      }
                    </Typography>
                  </Stack>
                );
              }
            }}
            IconComponent={props => (
              <Box
                {...props}
                sx={{ top: 'calc(50% - 0.7em) !important', right: '27px !important' }}
              >
                <ExpandMoreIcon
                  sx={{ color: theme.palette.typography.color.secondary }}
                />
              </Box>
            )}
          >
            {cinemasList.map(cinema => (
              <MenuItem
                key={cinema.id}
                value={cinema.id}
                disabled={
                  (cinema.id === 0 && selectedCinemas.find(id => id === 0) > -1) ||
                  (selectedCinemas.length === 1 && cinema.id === selectedCinemas[0])
                }
              >
                <Checkbox checked={selectedCinemas.find(id => id === cinema.id) > -1} />
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        lineHeight: '24px',
                        fontWeight: '400',
                        fontSize: {
                          mobile: '16px',
                          tablet: pxToRem(16),
                        },
                      }}
                    >
                      {cinema.name}
                    </Typography>
                  }
                />
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>

      <MoviesList
        moviesList={moviesList}
        hasMore={hasMore}
        fetchMovies={fetchMovies}
        isFetching={isFetching}
        itemListName='Home'
      />
    </Container>
  );
};
